import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Globe, Facebook, Instagram } from 'lucide-react';
import QuoteForm from '../components/QuoteForm';

const GetQuotePage = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');

  const translations = {
    en: {
      title: "Request a Quote",
      subtitle: "Fill out the form below and we'll get back to you within 24 hours",
      backToHome: "Back to Home",
      form: {
        title: "Request a Quote",
        name: "Full Name",
        email: "Email Address",
        phone: "Phone Number",
        productUrl: "Product URL",
        description: "Product Description",
        quantity: "Quantity",
        address: "Delivery Address in Belize",
        submit: "Submit Request",
        success: "Quote request submitted! We'll contact you soon."
      }
    },
    es: {
      title: "Solicitar Cotización",
      subtitle: "Complete el formulario a continuación y nos comunicaremos con usted en 24 horas",
      backToHome: "Volver al Inicio",
      form: {
        title: "Solicitar Cotización",
        name: "Nombre Completo",
        email: "Correo Electrónico",
        phone: "Número de Teléfono",
        productUrl: "URL del Producto",
        description: "Descripción del Producto",
        quantity: "Cantidad",
        address: "Dirección de Entrega en Belice",
        submit: "Enviar Solicitud",
        success: "¡Solicitud enviada! Te contactaremos pronto."
      }
    }
  };

  const t = translations[language];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f7f5f2] to-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/90 backdrop-blur-md shadow-sm z-50">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#8b1538] hover:text-[#6b0f2a]"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">{t.backToHome}</span>
            </Button>
            
            <img 
              src="https://customer-assets.emergentagent.com/job_57e2dd96-4e50-451b-8abf-042890e7a51b/artifacts/m648krxt_Athena%27s%20Logo.png"
              alt="Athena's"
              className="h-12 w-auto object-contain cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
              className="flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              {language === 'en' ? 'ES' : 'EN'}
            </Button>
            <a 
              href="https://www.facebook.com/profile.php?id=61584846392424" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#8b1538] hover:text-[#6b0f2a] transition-colors"
            >
              <Facebook className="w-6 h-6" />
            </a>
            <a 
              href="https://www.instagram.com/athenasonlineshoppingservices/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#8b1538] hover:text-[#6b0f2a] transition-colors"
            >
              <Instagram className="w-6 h-6" />
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-24 pb-20 px-4">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-[#1e1919] mb-4">
              {t.title}
            </h1>
            <p className="text-lg text-[#736c64]">
              {t.subtitle}
            </p>
          </div>

          <QuoteForm language={language} translations={translations[language].form} />
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1e1919] text-white py-8 px-4">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center gap-6 mb-4">
            <a
              href="https://www.facebook.com/profile.php?id=61584846392424"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              className="text-[#fad24b] hover:text-white transition-colors"
            >
              <Facebook className="w-6 h-6" />
            </a>
            <a
              href="https://www.instagram.com/athenasonlineshoppingservices/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="text-[#fad24b] hover:text-white transition-colors"
            >
              <Instagram className="w-6 h-6" />
            </a>
          </div>
          <p className="text-[#bbb5ae] text-sm mb-3">
            Business Hours: 10 AM - 9 PM (7 Days)
          </p>
          <div className="mb-4 text-sm space-y-1">
            <p className="text-[#bbb5ae]">
              Phone: <a href="tel:+5016735717" data-testid="footer-phone-link" className="text-[#fad24b] hover:text-white transition-colors">6735717</a>
            </p>
            <p className="text-[#bbb5ae]">
              WhatsApp: <a href="https://wa.me/5016735717" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="text-[#fad24b] hover:text-white transition-colors">Chat with us</a>
            </p>
            <p className="text-[#bbb5ae]">
              Email: <a href="mailto:athenasonlineshoppingservices@gmail.com" data-testid="footer-email-link" className="text-[#fad24b] hover:text-white transition-colors break-all">athenasonlineshoppingservices@gmail.com</a>
            </p>
            <p className="text-[#bbb5ae]">
              <a href="/privacy" data-testid="footer-privacy-link" className="text-[#fad24b] hover:text-white transition-colors">Privacy Policy</a>
            </p>
          </div>
          <p className="text-[#bbb5ae] text-sm">
            © {new Date().getFullYear()} Athena's Online Shopping Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default GetQuotePage;
